import { useMemo } from "react";
import { useSnapshot } from "valtio";
import { Ad } from "@models/Ad.model";
import { ContentCatalogFolder } from "@models/contentCatalogFolder.model";
import { contentCatalogStore } from "../contentCatalogStore";
import CollectionAccordion from "./CollectionAccordion";
import EmptyState from "./EmptyState";
import type { AdMenuItemsFn, FolderMenuItemsFn } from "../contentCatalog";

interface AllContentViewProps {
    node: ContentCatalogFolder | null;
    view: string;
    onAdClick?: (ad: Ad) => void;
    adMenuItems?: AdMenuItemsFn;
    folderMenuItems?: FolderMenuItemsFn;
    enableDnd?: boolean;
}

export default function AllContentView({
    node,
    view,
    onAdClick,
    adMenuItems,
    folderMenuItems,
    enableDnd,
}: AllContentViewProps) {
    const { folders, folderSearch, query } = useSnapshot(contentCatalogStore);
    const isSearching = (query || "").trim().length > 0;

    const items = useMemo(() => {
        const base = node ? [node] : (folders as ContentCatalogFolder[]);
        if (!isSearching) return base;
        return base.filter((item) => folderSearch.visibleFolderIds.has(item.id));
    }, [node, folders, isSearching, folderSearch.visibleFolderIds]);

    return (
        <div className="content-area">
            <div className="col-accordion-list">
                {items.map((item) => (
                    <CollectionAccordion
                        key={item.id}
                        node={item}
                        section="all"
                        depth={0}
                        parentFolderId={null}
                        view={view}
                        onAdClick={onAdClick}
                        adMenuItems={adMenuItems}
                        folderMenuItems={folderMenuItems}
                        enableDnd={enableDnd}
                    />
                ))}
                {isSearching && items.length === 0 && (
                    <p className="col-accordion-empty">No matching folders or ads.</p>
                )}
                {/* A catalog with nothing in it used to be unreachable -- every org had at
                    least its migrated content -- so an empty list rendered as a blank pane.
                    Scoping a client to what it requested through Create (MPN-2136) makes
                    empty the normal state for a client that has not requested anything. */}
                {!isSearching && !node && items.length === 0 && (
                    <EmptyState message="Content you request through the Moti Store appears here once it's approved." />
                )}
            </div>
        </div>
    );
}
